import React, { useState } from 'react';
import { Phone, MessageCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { companyInfo, brands } from '../../data/mock';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    device: '',
    problem: ''
  });

  const whatsappLink = `https://wa.me/55${companyInfo.whatsapp}?text=Olá! Gostaria de um orçamento.`;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Encontra o nome da marca selecionada
    const deviceName = brands.find(b => b.id === formData.device)?.name || formData.device;
    
    // Monta a mensagem para o WhatsApp
    const message = `Olá! Gostaria de um orçamento.

*Nome:* ${formData.name}
*Telefone:* ${formData.phone}
*Dispositivo:* ${deviceName}
*Problema:* ${formData.problem}`;

    // Codifica a mensagem para URL
    const encodedMessage = encodeURIComponent(message);
    
    // Abre o WhatsApp com a mensagem
    window.open(`https://wa.me/55${companyInfo.whatsapp}?text=${encodedMessage}`, '_blank');
  };

  return (
    <section className="py-16 bg-[#2B3990]" id="contato">
      <div className="container mx-auto px-4">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div className="text-white">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Entre em Contato
              </h2>
              <p className="text-gray-300 mb-8">
                Preencha o formulário ao lado e você será direcionado para nosso WhatsApp com sua mensagem pronta para enviar!
              </p>

              <div className="space-y-6">
                <a 
                  href={whatsappLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-4 p-4 bg-[#8DC63F] rounded-xl hover:bg-[#7ab635] transition-colors"
                >
                  <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center">
                    <Phone size={24} className="text-[#8DC63F]" />
                  </div>
                  <div>
                    <p className="font-semibold text-lg">WhatsApp</p>
                    <p>{companyInfo.whatsappFormatted}</p>
                  </div>
                </a>

                <div className="p-6 bg-white/10 rounded-xl backdrop-blur-sm">
                  <h3 className="font-semibold text-[#8DC63F] mb-3">Endereço</h3>
                  <p className="text-gray-300">{companyInfo.fullAddress}</p>
                  <p className="text-gray-300 mt-2">Horário: {companyInfo.hours}</p>
                </div>

                <div className="p-6 bg-white/10 rounded-xl backdrop-blur-sm">
                  <h3 className="font-semibold text-[#8DC63F] mb-3">Marcas Atendidas</h3>
                  <div className="flex flex-wrap gap-2">
                    {brands.map(brand => (
                      <span key={brand.id} className="px-3 py-1 bg-white/20 rounded-full text-sm">
                        {brand.name}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-white rounded-2xl p-8 shadow-xl">
              <form onSubmit={handleSubmit} className="space-y-5">
                <h3 className="text-2xl font-bold text-[#2B3990] mb-6">Solicite um Orçamento</h3>
                
                <div>
                  <Label htmlFor="name" className="text-gray-700">Nome Completo *</Label>
                  <Input 
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Seu nome"
                    required
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="phone" className="text-gray-700">Seu WhatsApp *</Label>
                  <Input 
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="(11) 99999-9999"
                    required
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="device" className="text-gray-700">Dispositivo *</Label>
                  <Select 
                    value={formData.device} 
                    onValueChange={(value) => setFormData(prev => ({ ...prev, device: value }))}
                    required
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Selecione o tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      {brands.map(brand => (
                        <SelectItem key={brand.id} value={brand.id}>
                          {brand.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="problem" className="text-gray-700">Descreva o Problema *</Label>
                  <Textarea 
                    id="problem"
                    name="problem"
                    value={formData.problem}
                    onChange={handleChange}
                    placeholder="Ex: Tela quebrada, não liga, bateria viciada..."
                    required
                    className="mt-1 min-h-[100px]"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-[#25D366] hover:bg-[#20bd5a] text-white font-semibold py-6"
                >
                  <MessageCircle size={20} className="mr-2" />
                  Enviar pelo WhatsApp
                </Button>

                <p className="text-xs text-gray-500 text-center">
                  Ao clicar, você será redirecionado para o WhatsApp com sua mensagem pronta.
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
